import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { useModal } from '@/context/ModalContext'
import { CONTACT } from '@/data/contact'
import { INSTALL_GUIDE_HASH, POS_WINDOWS_DOWNLOAD_URL } from '@/data/apps'
import { BrowserWarningMock } from './mocks/BrowserWarningMock'
import { SmartScreenMock } from './mocks/SmartScreenMock'
import { windowsUiLang } from './mocks/windows-strings'

const STEP_KEYS = ['browser', 'smartscreen', 'runAnyway', 'installer', 'connect'] as const

export function InstallGuideModal() {
  const { t, i18n } = useTranslation()
  const { installOpen, installSource, openInstall, closeInstall } = useModal()
  const winLang = windowsUiLang(i18n.language)

  // The back office links to /#install-windows; open the guide on load and on hash change.
  useEffect(() => {
    const sync = () => {
      if (window.location.hash !== INSTALL_GUIDE_HASH) return
      document.getElementById('apps')?.scrollIntoView()
      openInstall('hash')
    }
    sync()
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [openInstall])

  const handleOpenChange = (open: boolean) => {
    if (open) return
    closeInstall()
    // Drop the hash so a reload does not reopen the guide and the same link works again.
    if (window.location.hash === INSTALL_GUIDE_HASH) {
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }

  const visuals: Partial<Record<(typeof STEP_KEYS)[number], ReactNode>> = {
    browser: <BrowserWarningMock lang={winLang} />,
    smartscreen: <SmartScreenMock step="collapsed" lang={winLang} />,
    runAnyway: <SmartScreenMock step="expanded" lang={winLang} />,
  }

  return (
    <Dialog open={installOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl bg-white p-0 shadow-2xl">
        <div className="h-1 bg-gradient-to-r from-primary via-primary-light to-primary/40" />
        <div className="px-6 pt-7 pb-8 sm:px-8">
          <DialogTitle className="text-[22px] sm:text-[24px] font-bold leading-tight tracking-tight text-foreground">
            {t('installGuide.title')}
          </DialogTitle>
          <DialogDescription className="mt-1.5 text-[14px] leading-relaxed text-foreground/55">
            {t(installSource === 'hash' ? 'installGuide.subtitleHash' : 'installGuide.subtitle')}
          </DialogDescription>
          {installSource === 'hash' && (
            <a
              href={POS_WINDOWS_DOWNLOAD_URL}
              download
              className="mt-4 inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-5 text-[15px] font-semibold text-white transition-colors hover:bg-primary/90"
            >
              {t('apps.pos.button')}
            </a>
          )}

          <ol className="mt-6 space-y-6">
            {STEP_KEYS.map((key, i) => (
              <li key={key} className="grid gap-3 sm:grid-cols-[1fr_260px] sm:items-start">
                <div className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[13px] font-bold text-primary">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-[15px] font-semibold text-foreground">{t(`installGuide.steps.${key}.title`)}</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-foreground/60">
                      {t(`installGuide.steps.${key}.text`)}
                    </p>
                  </div>
                </div>
                {visuals[key] && <div className="pl-10 sm:pl-0">{visuals[key]}</div>}
              </li>
            ))}
          </ol>

          <div className="mt-7 space-y-2 border-t border-black/5 pt-5 text-[13px] text-foreground/60">
            <p>
              {t('installGuide.retry')}{' '}
              <a href={POS_WINDOWS_DOWNLOAD_URL} download className="font-medium text-primary hover:underline">
                {t('installGuide.retryLink')}
              </a>
            </p>
            <p>
              <a href={CONTACT.telegram.href} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                {t('installGuide.help')}
              </a>
            </p>
          </div>

          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="mt-6 w-full rounded-xl bg-primary py-3 text-[15px] font-semibold text-white transition-colors hover:bg-primary/90"
          >
            {t('installGuide.close')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
